import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { getList, deleteItem, getPresignedUrl } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { BsFillPlusCircleFill } from "react-icons/bs";
import DocumentVerificationUpdateModal from "./DocumentVerificationUpdateModal";
import DocumentVerificationDeleteModal from "./DocumentVerificationDeleteModal";
import DocumentVerification from "../../models/document-verification";
import Asset from "../../models/asset";
import {
  DOCUMENT_VERIFICATIONS_CREATE,
  DOCUMENT_VERIFICATIONS_DESTROY,
  DOCUMENT_VERIFICATIONS_UPDATE,
} from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import DocumentVerificationCopyModal from "./DocumentVerificationCopyModal";

const ITEMS_PER_PAGE = 10;

const DocumentVerificationCrud = () => {
  const fields = [
    {
      key: "tripNumber",
      _classes: ["text-center"],
      label: "Número de Viaje",
    },
    {
      key: "username",
      _classes: ["text-center"],
      label: "Usuario",
    },
    {
      key: "date",
      _classes: ["text-center"],
      label: "Fecha",
    },
    {
      key: "area",
      _classes: ["text-center"],
      label: "Área",
    },
    {
      key: "files",
      _classes: ["text-center"],
      label: "Archivos",
      filter: false,
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [verifications, setVerifications] = useState<DocumentVerification[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedVerification, setSelectedVerification] =
    useState<DocumentVerification | null>(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [itemCount, setItemCount] = useState(0);
  const [downloadingFiles, setDownloadingFiles] = useState<{
    [key: number]: { pdf?: boolean; excel?: boolean };
  }>({});

  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );

  const fetchVerifications = async (
    page = currentPage,
    searchTerm = search
  ) => {
    setLoading(true);
    const limit = ITEMS_PER_PAGE;
    const offset = page > 0 ? ITEMS_PER_PAGE * (page - 1) : 0;

    const params = new Map();

    params.set("company_id", companyId);

    if (searchTerm) {
      params.set("search", searchTerm);
    }

    try {
      const response = await getList<DocumentVerification>(
        "/document_verifications/",
        limit,
        offset,
        params
      );

      if (response.status === SUCCESS) {
        setVerifications(response.data!.items!);
        const count = response.data!.count;
        setItemCount(count);
        setTotalPages(Math.ceil(count / ITEMS_PER_PAGE));
      } else {
        warningAlert("Error al cargar las verificaciones.");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      warningAlert("Error al cargar las verificaciones.");
    } finally {
      setLoading(false);
    }
  };

  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (id: number) => {
    setDeleting(true);
    try {
      const response = await deleteItem(`/document_verifications/${id}`, {});

      if (response.status === SUCCESS) {
        successAlert("Verificación eliminada con éxito.");

        const newCount = itemCount - 1;
        const newTotalPages = Math.ceil(newCount / ITEMS_PER_PAGE);
        const newPage =
          currentPage > newTotalPages
            ? Math.max(1, newTotalPages)
            : currentPage;

        setItemCount(newCount);
        setTotalPages(newTotalPages);

        // Only fetch if page changed
        if (newPage !== currentPage) {
          setCurrentPage(newPage);
          fetchVerifications(newPage);
        } else {
          // Local update if staying on same page
          setVerifications((prev) => prev.filter((item) => item.id !== id));
        }
      } else {
        warningAlert("Error al eliminar la verificación.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      warningAlert("Error al eliminar la verificación.");
    } finally {
      setShowDeleteModal(false);
      setSelectedVerification(null);
      setDeleting(false);
    }
  };

  const handleDownloadPdf = async (verificationId: number) => {
    setDownloadingFiles((prev) => ({
      ...prev,
      [verificationId]: { ...prev[verificationId], pdf: true },
    }));

    try {
      const response = await getPresignedUrl(
        `/document_verifications/${verificationId}/reports/pdf/`
      );

      if (response.status === SUCCESS && response.data?.url) {
        window.open(response.data.url, "_blank");
      } else {
        warningAlert("Error al obtener el reporte PDF.");
      }
    } catch (error) {
      console.error("PDF download error:", error);
      warningAlert("Error al descargar el reporte PDF.");
    } finally {
      setDownloadingFiles((prev) => ({
        ...prev,
        [verificationId]: { ...prev[verificationId], pdf: false },
      }));
    }
  };

  const handleDownloadExcel = async (verificationId: number) => {
    setDownloadingFiles((prev) => ({
      ...prev,
      [verificationId]: { ...prev[verificationId], excel: true },
    }));

    try {
      const response = await getPresignedUrl(
        `/document_verifications/${verificationId}/reports/excel/`
      );

      if (response.status === SUCCESS && response.data?.url) {
        window.open(response.data.url, "_blank");
      } else {
        warningAlert("Error al obtener el reporte Excel.");
      }
    } catch (error) {
      console.error("Excel download error:", error);
      warningAlert("Error al descargar el reporte Excel.");
    } finally {
      setDownloadingFiles((prev) => ({
        ...prev,
        [verificationId]: { ...prev[verificationId], excel: false },
      }));
    }
  };

  const formatDate = (isoString: string) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const pad = (n: number) => n.toString().padStart(2, "0");

    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const onCreateClick = () => {
    setSelectedVerification(null);
    setShowUpdateModal(true);
  };

  useEffect(() => {
    fetchVerifications(currentPage, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, search]);

  useEffect(() => {
    const fetchAssets = async () => {
      const params = new Map();
      params.set("company_id", companyId);
      const response = await getList<Asset>("/assets/", 1000, 0, params);
      if (response.status === SUCCESS) {
        setAssets(response.data?.items || []);
      } else {
        warningAlert("Error al cargar los activos.");
      }
    };

    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CCard>
      <CCardHeader>
        <CRow>
          <CCol>
            <h3>Verificaciones de Documentos</h3>
          </CCol>
          <PermissionRequiredComponent
            permissionName={DOCUMENT_VERIFICATIONS_CREATE}
          >
            <CCol className="text-right">
              <CButton color="success" onClick={onCreateClick}>
                <BsFillPlusCircleFill className="mr-2" />
                Nueva Verificación
              </CButton>
            </CCol>
          </PermissionRequiredComponent>
        </CRow>
      </CCardHeader>
      <CCardBody>
        <form onSubmit={handleSearchSubmit}>
          <CRow className="mb-3">
            <CCol>
              <CInputGroup>
                <CInputGroupText>Buscar</CInputGroupText>

                <CInput
                  type="text"
                  placeholder="Buscar por número de viaje o usuario"
                  value={search}
                  onChange={handleSearchChange}
                />
              </CInputGroup>
            </CCol>
          </CRow>
        </form>
        <CDataTable
          items={verifications}
          fields={fields}
          loading={loading}
          hover
          striped
          scopedSlots={{
            date: (item: any) => (
              <td className="text-center">{formatDate(item.date)}</td>
            ),
            files: (item: any) => (
              <td className="text-center">
                <CButtonGroup>
                  <CButton
                    color="danger"
                    onClick={() => handleDownloadPdf(item.id)}
                    disabled={downloadingFiles[item.id]?.pdf}
                    title="Descargar PDF"
                  >
                    {downloadingFiles[item.id]?.pdf ? (
                      <i className="fa fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa fa-file-pdf"></i>
                    )}
                  </CButton>
                  <CButton
                    color="success"
                    onClick={() => handleDownloadExcel(item.id)}
                    disabled={downloadingFiles[item.id]?.excel}
                    title="Descargar Excel"
                  >
                    {downloadingFiles[item.id]?.excel ? (
                      <i className="fa fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa fa-file-excel"></i>
                    )}
                  </CButton>
                </CButtonGroup>
              </td>
            ),
            actions: (item: any) => (
              <td className="text-center">
                <CButtonGroup>
                  <PermissionRequiredComponent
                    permissionName={DOCUMENT_VERIFICATIONS_CREATE}
                  >
                    <CButton
                      color="primary"
                      onClick={() => {
                        setSelectedVerification(item);
                        setShowCopyModal(true);
                      }}
                    >
                      <i className="fa fa-copy"></i>
                    </CButton>
                  </PermissionRequiredComponent>
                  <PermissionRequiredComponent
                    permissionName={DOCUMENT_VERIFICATIONS_UPDATE}
                  >
                    <CButton
                      color="info"
                      onClick={() => {
                        setSelectedVerification(item);
                        setShowUpdateModal(true);
                      }}
                    >
                      <i className="fa fa-pencil"></i>
                    </CButton>
                  </PermissionRequiredComponent>
                  <PermissionRequiredComponent
                    permissionName={DOCUMENT_VERIFICATIONS_DESTROY}
                  >
                    <CButton
                      color="danger"
                      onClick={() => {
                        setSelectedVerification(item);
                        setShowDeleteModal(true);
                      }}
                    >
                      <i className="fa fa-trash"></i>
                    </CButton>
                  </PermissionRequiredComponent>
                </CButtonGroup>
              </td>
            ),
          }}
        />
        <CPagination
          activePage={currentPage}
          pages={totalPages}
          onActivePageChange={handlePageChange}
          className="mt-3"
        />
      </CCardBody>

      {showCopyModal && (
        <DocumentVerificationCopyModal
          verification={selectedVerification || undefined}
          assets={assets}
          onClose={() => {
            setShowCopyModal(false);
            setSelectedVerification(null);
          }}
          onSuccess={() => {
            fetchVerifications();
            setShowCopyModal(false);
            setSelectedVerification(null);
          }}
        />
      )}

      {showUpdateModal && (
        <DocumentVerificationUpdateModal
          verification={selectedVerification || undefined}
          assets={assets}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedVerification(null);
          }}
          onSuccess={() => {
            fetchVerifications();
            setShowUpdateModal(false);
            setSelectedVerification(null);
          }}
        />
      )}

      {showDeleteModal && selectedVerification && (
        <DocumentVerificationDeleteModal
          verification={selectedVerification}
          deleting={deleting}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedVerification(null);
          }}
          onDelete={() => handleDelete(selectedVerification.id!)}
        />
      )}
    </CCard>
  );
};

export default DocumentVerificationCrud;
